// location.service.ts
import { isPlatformBrowser } from '@angular/common';
import {
  Inject,
  Injectable,
  NgZone,
  PLATFORM_ID,
  computed,
  signal,
} from '@angular/core';

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface GeoOptions {
  enableHighAccuracy?: boolean;
  maxAge?: number;
  timeout?: number;

  // Emulation options (Android-like)
  minUpdateDistanceMeters?: number; // default 10
  minUpdateIntervalMillis?: number; // default 15000
  fallbackPollIntervalMillis?: number | null; // default 15000, set null to disable fallback
}

@Injectable({
  providedIn: 'root',
})
export class GeoLocationService {
  private coords = signal<Coordinates | null>(null);
  public readonly currentCoords = computed(() => this.coords());

  private watchId: number | null = null;
  private fallbackTimer: number | null = null;

  // keep last emitted timestamp and last emitted coords for filtering
  private lastEmitTimestamp = 0;
  private lastEmittedCoords: Coordinates | null = null;

  // default options (similar to your Android snippet)
  private opts: Required<GeoOptions> = {
    enableHighAccuracy: true,
    maxAge: 0,
    timeout: 10000,
    minUpdateDistanceMeters: 10,
    minUpdateIntervalMillis: 15000,
    fallbackPollIntervalMillis: 15000,
  };

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private ngZone: NgZone
  ) {
    // initial behaviour: start tracking with defaults and fetch once
    this.start();
    // intentionally call but ignore rejection; it will still set coords if available
    this.getCurrentCoordinatesOnce().catch(() => null);
  }

  /**
   * Start tracking and optionally override defaults.
   * Calling start will stop any prior tracking first.
   */
  start(options?: GeoOptions) {
    if (!isPlatformBrowser(this.platformId)) return;

    // merge provided options with defaults
    this.opts = { ...this.opts, ...(options || {}) };

    // reset any previous tracking
    this.stopTracking();

    if (!('geolocation' in navigator)) {
      // geolocation not supported
      console.error('Geolocation is not supported by this browser.');
      return;
    }

    // try watchPosition first
    try {
      this.watchId = navigator.geolocation.watchPosition(
        position => {
          // watchPosition callbacks may be outside angular zone; ensure we run inside it
          this.ngZone.run(() => this.handleNewPosition(position));
        },
        err => {
          console.error('Geolocation watch error:', err);
          // fallback to polling if configured
          if (
            this.opts.fallbackPollIntervalMillis &&
            this.opts.fallbackPollIntervalMillis > 0
          ) {
            this.startFallbackPolling();
          }
        },
        {
          enableHighAccuracy: this.opts.enableHighAccuracy,
          maximumAge: this.opts.maxAge,
          timeout: this.opts.timeout,
        }
      );
    } catch (e) {
      console.error('Failed to start geolocation watchPosition:', e);
      if (
        this.opts.fallbackPollIntervalMillis &&
        this.opts.fallbackPollIntervalMillis > 0
      ) {
        this.startFallbackPolling();
      }
    }
  }

  stopTracking() {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.watchId !== null) {
      try {
        navigator.geolocation.clearWatch(this.watchId);
      } catch (e) {
        // ignore clearWatch errors
      }
      this.watchId = null;
    }

    if (this.fallbackTimer !== null) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  /**
   * Return current coordinates once (resolves null on failure)
   */
  getCurrentCoordinatesOnce(): Promise<Coordinates | null> {
    return new Promise(resolve => {
      if (!isPlatformBrowser(this.platformId)) {
        resolve(null);
        return;
      }

      if (!('geolocation' in navigator)) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        position => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          // update the signal inside the Angular zone
          this.ngZone.run(() => {
            this.coords.set(coords);
            this.lastEmittedCoords = coords;
            this.lastEmitTimestamp = Date.now();
          });
          resolve(coords);
        },
        err => {
          console.error('Error getting location:', err);
          resolve(null);
        },
        {
          enableHighAccuracy: this.opts.enableHighAccuracy,
          maximumAge: this.opts.maxAge,
        }
      );
    });
  }

  /**
   * Internal handler for positions coming from watchPosition or fallback.
   * Applies min distance + min interval filtering (like Android example).
   */
  private handleNewPosition(position: GeolocationPosition) {
    const coords: Coordinates = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };

    const now = Date.now();

    if (!this.lastEmittedCoords) {
      // first emission -> always emit
      this.emitCoords(coords);
      return;
    }

    const dist = this.haversineDistanceMeters(
      this.lastEmittedCoords.latitude,
      this.lastEmittedCoords.longitude,
      coords.latitude,
      coords.longitude
    );

    const timeDiff = now - this.lastEmitTimestamp;

    if (
      dist >= this.opts.minUpdateDistanceMeters ||
      timeDiff >= this.opts.minUpdateIntervalMillis
    ) {
      this.emitCoords(coords);
    } else {
      // ignore noisy update
      // (do not update lastEmittedCoords so we keep last "accepted" location)
      // optional: update an internal 'lastSeenRaw' if you want to keep raw stream
    }
  }

  private emitCoords(coords: Coordinates) {
    this.coords.set(coords);
    this.lastEmittedCoords = coords;
    this.lastEmitTimestamp = Date.now();
  }

  /**
   * Start simple polling using getCurrentPosition at configured interval.
   * Only used as fallback when watchPosition errors or is unreliable.
   */
  private startFallbackPolling() {
    if (this.fallbackTimer !== null) return;

    // immediate run + interval
    this.fallbackTimer = window.setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.ngZone.run(() => this.handleNewPosition(position));
        },
        err => {
          console.error('Fallback getCurrentPosition error:', err);
        },
        {
          enableHighAccuracy: this.opts.enableHighAccuracy,
          maximumAge: this.opts.maxAge,
        }
      );
    }, this.opts.fallbackPollIntervalMillis);

    // do an immediate one now
    navigator.geolocation.getCurrentPosition(
      position => {
        this.ngZone.run(() => this.handleNewPosition(position));
      },
      err => {
        console.error('Fallback immediate getCurrentPosition error:', err);
      },
      {
        enableHighAccuracy: this.opts.enableHighAccuracy,
        maximumAge: this.opts.maxAge,
      }
    );
  }

  /**
   * Haversine formula - returns meters between two lat/lng points
   */
  private haversineDistanceMeters(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (v: number) => (v * Math.PI) / 180;
    const R = 6371000; // meters
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
}
